import React, { useState } from 'react'
import { Stack, Tooltip, Typography } from '@mui/material'
import pluralize from 'pluralize'
import { ReactComponent as FiltersIcon } from '../../../../assets/images/Filters.svg'
import { NestedMultiselectFilter, NestedMultiselectFilterItem } from '../../NestedMultiselectFilter'

type DataTableFiltersButtonProps = {
    items: NestedMultiselectFilterItem[]
    selectedItemIds: string[]
    onSelectionChange: (selectedItemIds: string[]) => void
    appliedFiltersCount: number
    hiddenColumnsCount?: number
    onResetFilters?: () => void
    canResetFilters?: boolean
    menuWidth?: number
}

/** Reports hidden columns too, or one would vanish with nothing on screen saying why. */
const getFiltersSummary = (appliedFiltersCount: number, hiddenColumnsCount: number) => {
    const parts = []

    if (appliedFiltersCount) {
        parts.push(`${appliedFiltersCount} ${pluralize('filter', appliedFiltersCount)} applied`)
    }

    if (hiddenColumnsCount) {
        parts.push(`${hiddenColumnsCount} ${pluralize('column', hiddenColumnsCount)} hidden`)
    }

    return parts.join(' · ') || 'Apply filters'
}

export const DataTableFiltersButton = ({
    items,
    selectedItemIds,
    onSelectionChange,
    appliedFiltersCount,
    hiddenColumnsCount = 0,
    onResetFilters,
    canResetFilters = true,
    menuWidth,
}: DataTableFiltersButtonProps) => {
    const [isTooltipOpen, setIsTooltipOpen] = useState(false)
    const [isPopoverOpen, setIsPopoverOpen] = useState(false)

    const isHighlighted = !!appliedFiltersCount || !!hiddenColumnsCount

    return (
        <Tooltip
            placement="top-start"
            arrow
            open={isTooltipOpen && !isPopoverOpen}
            onOpen={() => {
                if (!isPopoverOpen) {
                    setIsTooltipOpen(true)
                }
            }}
            onClose={() => setIsTooltipOpen(false)}
            disableHoverListener={isPopoverOpen}
            title={
                <Typography
                    variant={isHighlighted ? '14x600' : '14x400'}
                    color={isHighlighted ? 'figma.primary' : 'figma.grayscale.textHier1'}
                >
                    {getFiltersSummary(appliedFiltersCount, hiddenColumnsCount)}
                </Typography>
            }
            componentsProps={{
                tooltip: {
                    sx: {
                        backgroundColor: 'figma.grayscale.contentWrapper',
                        px: 2,
                        py: 0.5,
                        borderRadius: '6px',
                        boxShadow: '0px 4px 8px 0px rgba(0, 0, 0, 0.20)',
                    },
                },
                arrow: {
                    sx: {
                        color: 'figma.grayscale.contentWrapper',
                    },
                },
            }}
        >
            <Stack onClick={() => setIsTooltipOpen(false)}>
                <NestedMultiselectFilter
                    icon={FiltersIcon}
                    items={items}
                    selectedItemIds={selectedItemIds}
                    onSelectionChange={onSelectionChange}
                    isHighlighted={isHighlighted}
                    onReset={onResetFilters}
                    canReset={canResetFilters}
                    menuWidth={menuWidth}
                    onOpenChange={(open) => {
                        setIsPopoverOpen(open)
                        if (open) setIsTooltipOpen(false)
                    }}
                />
            </Stack>
        </Tooltip>
    )
}

export default DataTableFiltersButton
